import React, { useState } from "react";
import FadeIn from "react-fade-in";
import { useLocation } from "react-router-dom";
import MoodCaptureForm from "./QuestionnaireForms/MoodCapture";
import FilledButton from "../../../InterstellrComponents/FilledButton";
import axios from "axios";
import {
  GET_NEXT_QUESTION,
  SUBMIT_QUESTIONNAIRE_ANSWER,
} from "../../../Routes";
import { generateURLOptions } from "../../../URLOptions";
import SingleChoiceCapture from "./QuestionnaireForms/SingleChoiceCapture";
import MultipleChoiceCapture from "./QuestionnaireForms/MultipleChoiceCapture";
import TextCapture from "./QuestionnaireForms/TextCapture";

const AnswerQuestionnaire = () => {
  const { state } = useLocation();

  const [questionnaire, setQuestionnaire] = useState(
    state?.questionnaire ?? {},
  );

  const [selectedMood, setSelectedMood] = useState(2);
  const [responseText, setResponseText] = useState("");
  const [selectedSingleOption, setSelectedSingleOption] = useState(null); // Changed from {} to null
  const [selectedMultipleOptions, setSelectedMultipleOptions] = useState([]);

  const submitQuestionnaireAnswer = async () => {
    const currentQuestion = questionnaire?.questions?.[0];

    await axios
      .post(
        SUBMIT_QUESTIONNAIRE_ANSWER,
        {
          questionId: currentQuestion?._id ?? "",
          mood: currentQuestion?.type == 0 ? selectedMood : null,
          responseOptions:
            currentQuestion?.type == 1
              ? [selectedSingleOption?._id] // Send just the ID
              : currentQuestion?.type == 2
                ? selectedMultipleOptions.map((opt) => opt._id) // Send array of IDs
                : null,
          responseText:
            currentQuestion?.type == 3 ? responseText?.toString() : null,
        },
        {
          headers: generateURLOptions(),
        },
      )
      .then(async (response) => {
        if (response?.data?.success == false) {
          if (response.data?.data?.skip == true) {
            console.log("Answer already submitted. UI should auto-skip");
            return updateQuestionnaire();
          }
          return alert(response?.data?.message ?? "");
        }
        console.log("QUESTIONNAIRE RESPONSE DATA: ", response);
        updateQuestionnaire();
      })
      .catch((error) => {
        console.log("Save Questionnaire Answer Error: ", error);
        return alert(error);
      });
  };

  const updateQuestionnaire = async () => {
    await axios
      .get(GET_NEXT_QUESTION + questionnaire?._id, {
        headers: generateURLOptions(),
      })
      .then(async (response) => {
        if (response?.data?.success == false) {
          return alert(response?.data?.message ?? "");
        }

        console.log("Questionnaire Data: ", response.data.data);

        setQuestionnaire(response.data.data);

        // Reset form states when moving to next question
        setSelectedMood(2);
        setResponseText("");
        setSelectedSingleOption(null);
        setSelectedMultipleOptions([]);
      })
      .catch((error) => {
        console.log("Save Questionnaire Answer Error: ", error);
        return alert(error);
      });
  };

  const currentQuestion = questionnaire?.questions?.[0];

  return (
    <>
      <div className="container mx-auto flex flex-col justify-center items-center h-full w-full px-4">
        <div className="w-full max-w-3xl">
          <FadeIn delay={200}>
            <div className="flex flex-col mb-4">
              {questionnaire?.questions?.length > 0 ? (
                <div className="flex flex-col">
                  {/* Header with progress */}
                  <div className="flex flex-row items-center mb-6">
                    <h4 className="text-black font-medium text-lg">
                      {questionnaire?.name ?? ""}
                    </h4>

                    <div style={{ flex: 1 }} />

                    <div className="flex flex-row justify-center items-center gap-2">
                      <p className="font-medium text-black text-base">
                        Question {(questionnaire?.totalAnswered ?? 0) + 1} of{" "}
                        {questionnaire?.totalQuestions ?? 0}
                      </p>
                    </div>
                  </div>

                  {/* Render the Dynamic Content Fields here */}

                  {/* Render the Mood Capture Form */}
                  {currentQuestion?.type == 0 ? (
                    <div className="min-h-[50vh] flex justify-center items-center">
                      <MoodCaptureForm
                        selectedMood={selectedMood}
                        onChange={setSelectedMood}
                      />
                    </div>
                  ) : null}

                  {/* Render the Single Choice Capture Form */}
                  {currentQuestion?.type == 1 ? (
                    <div className="min-h-[50vh] flex justify-center items-center">
                      <SingleChoiceCapture
                        question={currentQuestion}
                        selected={selectedSingleOption}
                        onChange={setSelectedSingleOption}
                      />
                    </div>
                  ) : null}

                  {/* Render the Multiple Choice Capture Form */}
                  {currentQuestion?.type == 2 ? (
                    <div className="min-h-[50vh] flex justify-center items-center">
                      <MultipleChoiceCapture
                        question={currentQuestion}
                        selected={selectedMultipleOptions}
                        onChange={setSelectedMultipleOptions}
                      />
                    </div>
                  ) : null}

                  {/* Render the Text Capture Form */}
                  {currentQuestion?.type == 3 ? (
                    <div className="min-h-[50vh] flex justify-center items-center">
                      <TextCapture
                        question={currentQuestion}
                        value={responseText}
                        onChange={setResponseText}
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-center py-12">
                  <h3 className="text-2xl font-semibold text-black mb-2">
                    Questionnaire Complete!
                  </h3>
                  <p className="text-gray-600">
                    Thank you for completing this questionnaire.
                  </p>
                </div>
              )}
            </div>

            {/* Bottom Action Buttons */}
            {questionnaire?.questions?.length > 0 && (
              <div className="flex flex-row justify-center items-center gap-4">
                {/* Skip Button */}
                <div className="w-full md:w-[48%]">
                  <FilledButton
                    text={"Skip"}
                    textColor={"#181818"}
                    backgroundColor={"#ffffff"}
                    onPress={updateQuestionnaire}
                  />
                </div>

                {/* Continue Button */}
                <div className="w-full md:w-[48%]">
                  <FilledButton
                    text={
                      questionnaire?.questions?.length > 1 ? "Next" : "Complete"
                    }
                    onPress={submitQuestionnaireAnswer}
                  />
                </div>
              </div>
            )}
          </FadeIn>
        </div>
      </div>
    </>
  );
};

export default AnswerQuestionnaire;
